import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, LogOut, User as UserIcon, Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SiteLanguageSwitcher from "@/components/SiteLanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  const navItem = (to, label, testId) => {
    const active = pathname === to;
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`px-3 py-1.5 text-sm transition-colors rounded-none ${
          active ? "text-zinc-900 font-medium" : "text-zinc-500 hover:text-zinc-900"
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-40 glass-header border-b border-zinc-200/60" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group notranslate" data-testid="brand-link" translate="no">
          <img
            src="https://customer-assets.emergentagent.com/job_resume-refine-10/artifacts/55prsd04_file_0000000091f471f4bd5d37a8933cba8f.png"
            alt="ResumeLab"
            className="h-8 w-auto hidden sm:block"
            style={{ objectFit: "contain", objectPosition: "left center", maxWidth: "210px" }}
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "inline-flex"; }}
          />
          <span className="flex items-center gap-2 sm:hidden notranslate" translate="no" style={{ display: "flex" }}>
            <span className="font-display font-extrabold text-xl tracking-tight notranslate" translate="no">
              <span className="brand-red">Resume</span><span className="brand-blue">Lab</span>
            </span>
          </span>
        </Link>
        <nav className="flex items-center gap-1">
          <div className="hidden md:flex items-center gap-1">
            {navItem("/", t("nav.home"), "nav-home")}
            {navItem("/evaluate", t("nav.evaluate"), "nav-evaluate")}
            {navItem("/evaluate/linkedin", t("nav.linkedin"), "nav-linkedin")}
            {navItem("/builder", t("nav.build"), "nav-builder")}
            {navItem("/interview/prep", t("nav.interview"), "nav-interview")}
            {navItem("/templates", t("nav.templates"), "nav-templates")}
            {navItem("/pricing", t("nav.pricing"), "nav-pricing")}
            {navItem("/blog", t("nav.blog"), "nav-blog")}
            {navItem("/history", t("nav.history"), "nav-history")}
            {user?.is_admin && navItem("/admin/builder", t("nav.admin"), "nav-admin")}
          </div>
          <div className="hidden md:block ml-2">
            <SiteLanguageSwitcher />
          </div>
          {/* Mobile menu trigger */}
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-trigger"
            aria-label="Open menu"
            className="md:hidden p-2 text-zinc-700 hover:text-zinc-900"
          >
            <Menu className="w-5 h-5" />
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="ml-3 inline-flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-100 transition-colors"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "inline-flex"; }}
                      className="w-7 h-7 object-cover ring-1 ring-zinc-200"
                    />
                  ) : null}
                  <span
                    className="w-7 h-7 bg-zinc-900 text-white items-center justify-center text-xs font-mono"
                    style={{ display: user.picture ? "none" : "inline-flex" }}
                  >
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </span>
                  {user.is_pro && (
                    <span className="ml-1 px-1.5 py-0.5 bg-[#DC1C22] text-white text-[9px] font-mono tracking-widest uppercase" data-testid="pro-badge">
                      PRO
                    </span>
                  )}
                  <span className="hidden sm:inline text-sm font-medium text-zinc-700 max-w-[120px] truncate">
                    {user.name?.split(" ")[0]}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56">
                <DropdownMenuLabel className="font-mono text-xs uppercase tracking-wider text-zinc-500">
                  {t("auth.welcome_back")}
                </DropdownMenuLabel>
                <DropdownMenuItem className="rounded-none flex-col items-start gap-0.5 cursor-default" disabled>
                  <span className="font-medium text-zinc-900">{user.name}</span>
                  <span className="text-xs text-zinc-500">{user.email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={async () => { navigate("/"); await logout(); }}
                  className="rounded-none cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.sign_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="ml-3 btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white"
            >
              <UserIcon className="w-4 h-4" /> {t("nav.sign_in")}
            </Link>
          )}
        </nav>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-white"
          data-testid="mobile-menu"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between border-b border-zinc-200">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5">
              <span className="font-display font-extrabold text-xl tracking-tight">
                <span className="brand-red">Resume</span><span className="brand-blue">Lab</span>
              </span>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
              aria-label="Close menu"
              className="p-2 text-zinc-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-6 py-6 flex flex-col gap-1">
            {[
              ["/", t("nav.home"), "home"],
              ["/evaluate", t("nav.evaluate"), "evaluate"],
              ["/evaluate/linkedin", t("nav.linkedin"), "linkedin"],
              ["/builder", t("nav.build"), "build"],
              ["/interview/prep", t("nav.interview"), "interview"],
              ["/templates", t("nav.templates"), "templates"],
              ["/pricing", t("nav.pricing"), "pricing"],
              ["/blog", t("nav.blog"), "blog"],
              ["/history", t("nav.history"), "history"],
            ].map(([to, label, key]) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className="py-3 border-b border-zinc-100 text-lg font-display font-bold text-zinc-900"
                data-testid={`mobile-nav-${key}`}
              >
                {label}
              </Link>
            ))}
            <div className="mt-4 pt-4 border-t border-zinc-200">
              <SiteLanguageSwitcher />
            </div>
            <Link
              to="/evaluate"
              onClick={() => setMobileOpen(false)}
              className="mt-6 btn-sharp bg-[#DC1C22] text-white"
            >
              {t("landing.hero_cta_primary")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
